import { useSnackbar } from 'notistack';
import React from 'react';
import type { GetDataError } from 'restful-react';
import * as Yup from 'yup';
import {
  type Location,
  LocationSummary,
  type ResponseApiErrors,
  useGetOrganisationLocations,
  useMoveChargePoint,
  useUpdateChargePoint,
} from '../../../../../../../app/ApiGen';
import { LocationInput, LocationValue } from '../../../../../../../components/CustomFields/Location';
import { SwitchValue } from '../../../../../../../components/CustomFields/Switch';
import { errorSnackbar } from '../../../../../../../components/ErrorSnackbar';
import { Form } from '../../../../../../../components/Form';
import { Card, type OnSubmit } from '../../../../../../../design-system';
import { locationHasAttributes } from '../../../../../../../utils/format';
import type { FormFields } from '../../../../../../../utils/formUtils';
import { normalise, type Normalised } from '../../../../../../../utils/request';
import { useChargePoint } from '../../Provider';
import { TokenRequiredSwitchInput } from './customFormFields/TokenRequiredSwitchInput';

type ChargePointDetails = {
  name: string;
  locationId: string;
  tokenRequired: boolean;
  referenceId?: string;
};

const validationSchema = Yup.object().shape<ChargePointDetails>({
  name: Yup.string().required('Name is required').min(3).max(80),
  locationId: Yup.string().required('Location is required'),
  referenceId: Yup.string()
    .min(1)
    .max(80)
    .matches(
      /^[a-zA-Z0-9-_. ]{0,80}$/,
      'Reference ID must only include numbers, letters, ".", "-", "_", or " " characters.',
    ),
  tokenRequired: Yup.boolean().required('Must specify if a token is required'),
});

const getFormFields = (args: {
  organisationSlug: string;
  location: Normalised<LocationSummary> | undefined;
  locations: Normalised<Location>[] | undefined;
  locationsLoading: boolean;
  locationsError: GetDataError<ResponseApiErrors> | null;
}): FormFields<ChargePointDetails> => {
  const { location, locations, locationsLoading, locationsError, organisationSlug } = args;
  return {
    name: { id: 'name', label: 'Name' },
    locationId: {
      id: 'locationId',
      label: 'Location',
      valueComponent: () => <LocationValue value={location} organisationSlug={organisationSlug} />,
      inputComponent: ({
        disabled,
        error: inputError,
        helperText,
        value,
        name: inputName,
        multiple,
        checked,
        onChange,
        onBlur,
      }) => (
        <LocationInput
          disabled={disabled}
          error={inputError}
          helperText={helperText}
          value={value}
          name={inputName}
          multiple={multiple}
          checked={checked}
          onChange={onChange}
          onBlur={onBlur}
          dataLoading={locationsLoading}
          dataError={locationsError}
          options={locations}
        />
      ),
    },
    tokenRequired: {
      id: 'tokenRequired',
      label: 'Authorisation required',
      valueComponent: SwitchValue,
      inputComponent: TokenRequiredSwitchInput,
      inputProps: { helperText: 'Whether a valid RFID card must be swiped before a charging session can begin' },
    },
    referenceId: { id: 'referenceId', label: 'Reference ID' },
  };
};

export const DetailsForm: React.FC<{ orgSlug: string; chargePointId: string }> = ({ chargePointId, orgSlug }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { chargePoint, loading, refetch } = useChargePoint();
  const {
    loading: locationsLoading,
    error: locationsError,
    data,
  } = useGetOrganisationLocations({
    orgSlug,
  });
  const locations = data ? normalise(data) : undefined;
  const updateChargePoint = useUpdateChargePoint({ id: chargePointId });
  const updateChargePointLocation = useMoveChargePoint({ id: chargePointId });

  const [isEditing, setIsEditing] = React.useState(false);

  const formFields = getFormFields({
    organisationSlug: orgSlug,
    location: chargePoint?.location && locationHasAttributes(chargePoint.location) ? chargePoint.location : undefined,
    locations,
    locationsLoading,
    locationsError,
  });

  const initialValues: ChargePointDetails = {
    name: chargePoint?.name ?? '',
    locationId: chargePoint?.location?.id ?? '',
    tokenRequired: !!chargePoint?.tokenRequired,
    referenceId: chargePoint?.referenceId ?? '',
  };

  React.useEffect(() => {
    if (updateChargePoint.error) {
      errorSnackbar('Unable to edit charge point details', closeSnackbar, enqueueSnackbar, updateChargePoint.error);
    }
    if (updateChargePointLocation.error) {
      errorSnackbar(
        'Unable to edit charge point location',
        closeSnackbar,
        enqueueSnackbar,
        updateChargePointLocation.error,
      );
    }
  }, [closeSnackbar, enqueueSnackbar, updateChargePoint.error, updateChargePointLocation.error]);

  const onSubmit = React.useCallback<OnSubmit<ChargePointDetails>>(
    async (update, { setSubmitting }) => {
      try {
        await updateChargePoint.mutate({
          data: {
            id: chargePointId,
            type: 'chargePoints',
            attributes: { name: update.name, tokenRequired: update.tokenRequired, referenceId: update.referenceId },
          },
        });
        if (update.locationId !== chargePoint?.location?.id) {
          await updateChargePointLocation.mutate({ data: { id: update.locationId, type: 'locations' } });
        }
        enqueueSnackbar('Charge point updated', { variant: 'success' });
        setIsEditing(false);
        refetch();
      } finally {
        setSubmitting(false);
      }
    },
    [chargePoint?.location?.id, chargePointId, enqueueSnackbar, refetch, updateChargePoint, updateChargePointLocation],
  );

  return (
    <Card title="Details">
      <Form<ChargePointDetails>
        fields={formFields}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        isLoading={loading}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
      />
    </Card>
  );
};
